import React, { useState } from 'react';
import Modal from './Modal';
import { partnersApi, BulkImportResult } from '../api/partners';

/**
 * The header's "Bulk import" button — download a spreadsheet template, fill
 * it in, upload it back. Every row lands `imported` (unclaimed) regardless
 * of what the CSV contains — see partners/views.py's `bulk_import` action.
 */
const BulkImportModal: React.FC<{
  formId: number;
  onClose: () => void;
  onImported: (result: BulkImportResult) => void;
}> = ({ formId, onClose, onImported }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const downloadTemplate = async () => {
    const blob = await partnersApi.downloadImportTemplate(formId);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'partners_import_template.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!file) return;
    setIsSubmitting(true);
    setErrorMessage('');
    setResult(null);
    try {
      const imported = await partnersApi.bulkImport(formId, file);
      setResult(imported);
      if (imported.imported > 0) onImported(imported);
    } catch (err) {
      const data = (err as { response?: { data?: BulkImportResult } })?.response?.data;
      if (data) {
        setResult(data);
      } else {
        setErrorMessage('Import failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const succeededCleanly = !!result && result.errors.length === 0 && result.imported > 0;

  return (
    <Modal
      title="Bulk import"
      hint="Download the template, add one row per school, then upload it here. Every row is added as unclaimed — a school confirms and fills in the rest when it claims its listing."
      onClose={onClose}
      testId="partners-bulk-import-modal"
      footer={
        <>
          <button
            type="button"
            className="sgk-btn sgk-btn--ghost sgk-btn--sm"
            onClick={onClose}
            data-testid="partners-bulk-import-cancel-button"
          >
            {succeededCleanly ? 'Done' : 'Cancel'}
          </button>
          <button
            type="button"
            className="sgk-btn sgk-btn--gold sgk-btn--sm"
            disabled={!file || isSubmitting}
            onClick={handleImport}
            data-testid="partners-bulk-import-submit-button"
          >
            {isSubmitting ? 'Importing…' : 'Import'}
          </button>
        </>
      }
    >
      <div className="sgk-btnrow">
        <button
          type="button"
          className="sgk-btn sgk-btn--ghost sgk-btn--sm"
          onClick={downloadTemplate}
          data-testid="partners-bulk-import-template-button"
        >
          Download template
        </button>
      </div>

      <div className="sgk-field" style={{ marginTop: 18 }}>
        <label htmlFor="partners-bulk-import-file">CSV or ZIP file</label>
        <input
          id="partners-bulk-import-file"
          type="file"
          accept=".csv,.zip"
          className="sgk-input"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          data-testid="partners-bulk-import-file-input"
        />
      </div>

      {errorMessage && (
        <div className="sgk-alert sgk-alert--error" data-testid="partners-bulk-import-error">
          {errorMessage}
        </div>
      )}

      {result && (
        <div
          className={`sgk-alert ${result.errors.length === 0 ? 'sgk-alert--success' : 'sgk-alert--error'}`}
          data-testid="partners-bulk-import-result"
        >
          {result.error && <div>{result.error}</div>}
          {result.imported > 0 && (
            <div>{result.imported} school{result.imported === 1 ? '' : 's'} imported.</div>
          )}
          {result.errors.length > 0 && (
            <ul className="sgk-errorlist" data-testid="partners-bulk-import-error-list">
              {result.errors.slice(0, 20).map((e, i) => (
                <li key={i}>
                  Row {e.row}{e.field ? ` (${e.field})` : ''}: {e.message}
                </li>
              ))}
              {result.errors.length > 20 && <li>...and {result.errors.length - 20} more.</li>}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
};

export default BulkImportModal;
